
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function MelkbosstrandPage() {
  useEffect(() => {
    document.title = 'Melkbosstrand Painters Cape Town | Coastal Painting Services | Beach Property Protection';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional coastal painting services in Melkbosstrand, Cape Town. Expert beach property painting with marine-grade protection. Specialized coastal painting contractors.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Melkbosstrand painters, coastal painting, beach property protection Cape Town');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/melkbosstrand`);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Melkbosstrand Painters Cape Town",
      "description": "Professional coastal painting services in Melkbosstrand, Cape Town",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/melkbosstrand`,
      "areaServed": {
        "@type": "Place",
        "name": "Melkbosstrand, Cape Town, Western Cape, South Africa"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -33.7267,
          "longitude": 18.4503
        },
        "geoRadius": "10000"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Melkbosstrand Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c399d1283afad4082b3c13d2240f6132.jpg"
            alt="Professional coastal painting services in Melkbosstrand, Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Expert <span className="text-yellow-400">Melkbosstrand Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Specialized coastal painting services for Melkbosstrand beach properties. Marine-grade protection and expert craftsmanship for your seaside investment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Specialized Coastal Painting Services in Melkbosstrand
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert painting solutions designed for Melkbosstrand's unique beachfront environment, providing superior protection against salt air and coastal conditions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-wifi-line',
                title: 'Beach House Painting',
                description: 'Specialized painting for beachfront homes and holiday properties with marine-grade coatings and salt air protection.',
                features: ['Holiday homes', 'Beachfront properties', 'Salt air protection', 'UV resistance']
              },
              {
                icon: 'ri-water-percent-line',
                title: 'Marine Protection',
                description: 'Advanced marine-grade coatings and waterproofing systems designed specifically for coastal exposure.',
                features: ['Marine-grade coatings', 'Waterproofing', 'Corrosion protection', 'Weather sealing']
              },
              {
                icon: 'ri-building-2-line',
                title: 'Coastal Commercial',
                description: 'Professional painting for restaurants, guesthouses, and commercial properties serving the beach community.',
                features: ['Restaurants', 'Guesthouses', 'Beach facilities', 'Tourism properties']
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Melkbosstrand Properties Choose Our Coastal Expertise
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Beach Environment Expertise',
                    description: 'Specialized knowledge of coastal painting challenges including salt air corrosion, sand exposure, and marine conditions.'
                  },
                  {
                    title: 'Marine-Grade Solutions',
                    description: 'Premium marine-grade materials and coatings specifically designed to withstand harsh beachfront environments.'
                  },
                  {
                    title: 'Holiday Property Focus',
                    description: 'Understanding of holiday home needs with flexible scheduling and maintenance programs for seasonal properties.'
                  },
                  {
                    title: 'Long-term Protection',
                    description: 'Advanced protective systems ensuring your coastal investment maintains its value and appearance for years to come.'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20coastal%20painters%20working%20on%20Melkbosstrand%20beach%20property%2C%20marine-grade%20painting%20equipment%2C%20beachfront%20home%20maintenance%2C%20specialized%20coastal%20painting%20techniques%20Cape%20Town&width=600&height=700&seq=melkbosstrand-team-work&orientation=portrait"
                alt="Professional coastal painting in Melkbosstrand"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Melkbosstrand Beach Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our latest coastal painting projects in Melkbosstrand, demonstrating our expertise in beachfront property protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Luxury Beach Villa',
                location: 'Melkbosstrand Beachfront',
                description: 'Complete exterior renovation of a luxury beachfront villa with panoramic ocean views.',
                image: 'https://readdy.ai/api/search-image?query=Luxury%20beach%20villa%20painting%20in%20Melkbosstrand%20Cape%20Town%2C%20high-end%20beachfront%20home%20with%20ocean%20views%2C%20premium%20coastal%20painting%2C%20modern%20beach%20architecture&width=400&height=300&seq=melkbosstrand-project-1&orientation=landscape'
              },
              {
                title: 'Seaside Restaurant',
                location: 'Melkbosstrand Central',
                description: 'Professional painting renovation for a popular beachfront restaurant and entertainment venue.',
                image: 'https://readdy.ai/api/search-image?query=Seaside%20restaurant%20painting%20in%20Melkbosstrand%2C%20beachfront%20dining%20establishment%2C%20coastal%20commercial%20property%20painting%2C%20tourism%20facility%20Cape%20Town&width=400&height=300&seq=melkbosstrand-project-2&orientation=landscape'
              },
              {
                title: 'Holiday Home Complex',
                location: 'Melkbosstrand Estate',
                description: 'Comprehensive painting project for a 12-unit holiday home development.',
                image: 'https://readdy.ai/api/search-image?query=Holiday%20home%20complex%20painting%20in%20Melkbosstrand%2C%20vacation%20rental%20properties%2C%20coastal%20residential%20development%2C%20beach%20property%20maintenance%20Cape%20Town&width=400&height=300&seq=melkbosstrand-project-3&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              What Melkbosstrand Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied beachfront property owners in Melkbosstrand
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Emma Richardson',
                location: 'Melkbosstrand Beachfront',
                rating: 5,
                text: 'Outstanding work on our beach villa. The marine-grade coatings have held up perfectly against the salt air and ocean spray. Truly professional service.'
              },
              {
                name: 'Johan Kruger',
                location: 'Melkbosstrand Central',
                rating: 5,
                text: 'Excellent painting service for our beachfront restaurant. They understood the unique challenges of coastal commercial properties. Highly recommended.'
              },
              {
                name: 'Sarah Wilson',
                location: 'Melkbosstrand Estate',
                rating: 5,
                text: 'Professional and reliable service for our holiday home. The protective coatings give us peace of mind knowing our investment is well protected.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Protect Your Melkbosstrand Beach Property
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for specialized coastal painting consultation and marine-grade protection. Safeguard your beachfront investment with expert painting services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
